import * as React from 'react';
import type { ActorKind } from '@/types/api';
import { ACTOR_KIND, DEPLOY_STATUS, ENVIRONMENT, RUN_PHASE, RUN_PHASE_ORDER, TOOL_FAMILY } from '@/lib/copy';
import { formatDuration, formatMillis, shortSha } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { LedgerNotches, LedgerRail, LedgerStop, type Notch } from '@/components/ledger/rail';
import type { GlyphKind } from '@/components/ledger/glyph';
import { StateChip } from '@/components/state-chip';
import { HiddenGlyph } from '@/components/visibility-band';
import { Empty } from '@/components/ui/empty';
import { useStreamStore } from '@/stores/stream-store';
import {
  actionList,
  deployList,
  isLive,
  phaseIndex,
  stopList,
  type LedgerAction,
  type RunStop,
} from '@/stores/projection';

/* ---------------------------------------------------------------------------
   THE RUN LEDGER, expert edition.

   The same rail the client sees, with the same geometry and the same terminal
   glyphs — that shared device is the only thing making three separate apps read
   as one product. What differs is the content: the client gets plain-language
   phases, and the expert gets the tool stream.

   Two things this rail says that the client's cannot:

     * WHOSE RUN IT WAS. An expert-actor run produces no frames on the client
       stream at all, so a run the expert started is hidden work — and it carries
       the hidden marker, the same one the thread uses. Reading the ledger should
       answer "does the client know this happened" without any further clicks.

     * WHERE A FAILURE STOPPED. The notches are the contract's own server-derived
       phase projection, in order, so a failed run shows exactly which phase it
       died in rather than only that it died.

   Durations are measured, never estimated. A live stop ticks from the observed
   start; a finished one is fixed. There is no synthetic progress anywhere.
--------------------------------------------------------------------------- */

function glyphFor(stop: RunStop): GlyphKind {
  switch (stop.outcome) {
    case 'running':
      return 'running';
    case 'succeeded':
      return 'succeeded';
    case 'expired':
      return 'expired';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'failed';
  }
}

function notchesFor(stop: RunStop, now: number): Notch[] {
  const reached = phaseIndex(stop.phase);
  const terminal = stop.outcome !== 'running';

  return RUN_PHASE_ORDER.map((phase, index) => {
    const at = stop.phaseAt[phase];
    // A succeeded run passed everything. Anything else — live or failed — has a
    // boundary, and the notch at the boundary is the one the eye should land on:
    // for a live run it is what is happening, and for a failed one it is where it
    // stopped. That is the same treatment the promotion ledger gives a repo.
    const state: Notch['state'] =
      stop.outcome === 'succeeded'
        ? 'passed'
        : index < reached
          ? 'passed'
          : index === reached
            ? 'current'
            : 'future';

    return {
      key: phase,
      label: RUN_PHASE[phase],
      state,
      // Only a phase actually observed being entered gets a timing. A phase we
      // inferred past has no honest number to show.
      ...(at !== undefined
        ? { timing: formatMillis(Math.max(0, (index === reached && !terminal ? now : at) - stop.startedAt)) }
        : {}),
    };
  });
}

export function RunLedger({ className }: { className?: string }) {
  const ledger = useStreamStore((s) => s.ledger);
  const thinking = useStreamStore((s) => s.thinking);
  const stops = React.useMemo(() => stopList(ledger), [ledger]);
  const anyLive = stops.some(isLive);
  const now = useNow(anyLive);

  if (stops.length === 0) {
    return (
      <Empty
        title="No runs yet"
        body="A prompt in this thread starts a run. Runs the client started are here too — this is the whole history, not just yours."
        className={className}
      />
    );
  }

  // Newest first: on a live review the run that matters is the one running.
  const ordered = [...stops].reverse();

  return (
    <LedgerRail className={className}>
      {ordered.map((stop) => (
        <RunStopView key={stop.runId} stop={stop} now={now} thinking={thinking[stop.runId] ?? null} />
      ))}
    </LedgerRail>
  );
}

function RunStopView({ stop, now, thinking }: { stop: RunStop; now: number; thinking: string | null }) {
  const live = isLive(stop);
  const hiddenFromClient = stop.actorKind === 'expert';
  const actions = actionList(stop);
  const deploys = deployList(stop);

  return (
    <LedgerStop
      glyph={glyphFor(stop)}
      current={live}
      title={
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-mono text-xs text-ink">run {stop.runId}</span>
          <ActorMark actorKind={stop.actorKind} />
          {hiddenFromClient ? (
            <span
              className="inline-flex items-center gap-1 font-mono text-tiny uppercase tracking-[0.1em] text-hidden"
              title="An expert-actor run. It produces no frames on the client stream at all."
            >
              <HiddenGlyph className="h-[9px] w-[9px]" />
              hidden
            </span>
          ) : null}
          {stop.runTokens != null ? (
            <span className="font-mono text-tiny tabular-nums text-ink-3" title="Tokens this run consumed">
              {stop.runTokens.toLocaleString()} tok
            </span>
          ) : null}
        </span>
      }
      timing={formatDuration(
        new Date(stop.startedAt).toISOString(),
        stop.endedAt ? new Date(stop.endedAt).toISOString() : null,
        now,
      )}
      subtitle={
        <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
          <span
            className={cn(
              'font-mono text-tiny',
              live ? 'text-live' : stop.outcome === 'succeeded' ? 'text-ink-3' : 'text-danger',
            )}
          >
            {live || stop.outcome === 'succeeded'
              ? RUN_PHASE[stop.phase]
              : `${stop.outcome} in ${RUN_PHASE[stop.phase].toLowerCase()}`}
          </span>
          {stop.progress !== null ? (
            <span className="font-mono text-tiny tabular-nums text-ink-3">{Math.round(stop.progress * 100)}%</span>
          ) : null}
          {stop.messageSeq !== null ? (
            <span className="font-mono text-tiny text-ink-3">from turn #{stop.messageSeq}</span>
          ) : null}
        </span>
      }
    >
      <LedgerNotches notches={notchesFor(stop, now)} />

      {stop.waiting ? (
        <div className="mt-2 border-l-2 border-warn bg-warn-soft px-2.5 py-1.5">
          <p className="eyebrow text-warn">
            {stop.waiting.answerableByExpert ? 'Waiting on you' : 'Waiting on the client'}
          </p>
          <p className="mt-0.5 text-xs leading-[17px] text-ink">{stop.waiting.question}</p>
        </div>
      ) : null}

      {stop.errorCode ? (
        <div className="mt-2 border-l-2 border-danger bg-danger-soft px-2.5 py-1.5">
          <p className="font-mono text-tiny uppercase tracking-[0.08em] text-danger">{stop.errorCode}</p>
          {stop.errorMessage ? (
            <p className="mt-0.5 break-words text-xs leading-[17px] text-ink">{stop.errorMessage}</p>
          ) : null}
        </div>
      ) : null}

      {actions.length > 0 ? <ToolStream actions={actions} /> : null}

      {deploys.length > 0 ? (
        <ul className="m-0 mt-2 list-none space-y-1 p-0">
          {deploys.map((deploy) => (
            <li key={deploy.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="font-mono text-tiny text-ink-3">
                {ENVIRONMENT[deploy.environment].short}
                {deploy.repo_slug ? ` · ${deploy.repo_slug}` : ''}
              </span>
              <StateChip size="sm" state={DEPLOY_STATUS[deploy.status]} />
              {deploy.commit_sha ? (
                <span className="font-mono text-tiny text-ink-3">{shortSha(deploy.commit_sha)}</span>
              ) : null}
              {deploy.url && deploy.status === 'succeeded' ? (
                <a
                  href={deploy.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-tiny text-action underline underline-offset-2 hover:text-action-hi"
                >
                  open
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {thinking ? <ThinkingTrace text={thinking} /> : null}
    </LedgerStop>
  );
}

function ActorMark({ actorKind }: { actorKind: ActorKind }) {
  return (
    <span
      className={cn(
        'font-mono text-tiny uppercase tracking-[0.1em]',
        actorKind === 'expert' ? 'text-hidden' : 'text-ink-3',
      )}
    >
      {ACTOR_KIND[actorKind]}
    </span>
  );
}

/**
 * The tool stream.
 *
 * Collapsed to the last few by default. A long run produces hundreds of calls
 * and the ledger's job is to let a reviewer see the shape of what happened; the
 * full list is one click away and never the default, because burying the rail
 * under it defeats the device.
 */
function ToolStream({ actions }: { actions: LedgerAction[] }) {
  const [expanded, setExpanded] = React.useState(false);
  const visible = expanded ? actions : actions.slice(-6);
  const hidden = actions.length - visible.length;

  return (
    <div className="mt-2 border border-rule bg-raised">
      <div className="flex items-center justify-between gap-2 border-b border-rule px-2 py-1">
        <p className="eyebrow">Tool stream</p>
        <div className="flex items-center gap-2">
          <span className="font-mono text-tiny text-ink-3">{actions.length}</span>
          {actions.length > 6 ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="font-mono text-tiny uppercase tracking-[0.08em] text-action underline underline-offset-2"
            >
              {expanded ? 'Collapse' : `Show all`}
            </button>
          ) : null}
        </div>
      </div>

      {hidden > 0 ? (
        <p className="border-b border-rule px-2 py-0.5 font-mono text-tiny text-ink-3">
          {hidden} earlier call{hidden === 1 ? '' : 's'} not shown
        </p>
      ) : null}

      <ul className="m-0 list-none divide-y divide-rule p-0">
        {visible.map((action) => (
          <li key={action.actionId} className="flex items-baseline gap-2 px-2 py-[3px]">
            <span
              className="w-[38px] shrink-0 font-mono text-tiny uppercase tracking-[0.06em] text-ink-3"
              title={TOOL_FAMILY[action.family].detail}
            >
              {TOOL_FAMILY[action.family].label}
            </span>
            <span
              aria-hidden
              className={cn(
                'h-[5px] w-[5px] shrink-0 rounded-[1px]',
                action.status === 'running' && 'animate-notch-pulse bg-action',
                action.status === 'succeeded' && 'bg-ok',
                action.status === 'failed' && 'bg-danger',
                action.status === 'denied' && 'bg-warn',
              )}
            />
            <span className="min-w-0 flex-1 truncate text-xs text-ink-2" title={action.paths.join('  ')}>
              {action.summary}
            </span>
            <span className="shrink-0 font-mono text-tiny tabular-nums text-ink-3">
              {action.durationMs === null ? '' : formatMillis(action.durationMs)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The reasoning trace. Internal visibility, always — it reaches no client
    surface, which is exactly why an expert is allowed to read it. */
function ThinkingTrace({ text }: { text: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 font-mono text-tiny uppercase tracking-[0.1em] text-hidden"
      >
        <HiddenGlyph className="h-[9px] w-[9px]" />
        Reasoning trace {open ? '−' : '+'}
      </button>
      {open ? (
        <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap border-l-2 border-hidden-rule bg-hidden-soft px-2 py-1.5 font-mono text-tiny leading-[16px] text-ink-2">
          {text}
        </pre>
      ) : null}
    </div>
  );
}
